/** QuickRouter — Géométrie : placement des pastilles, formes de cuivre et distances.
 *
 * Toute forme de cuivre se ramène à l'une de deux primitives :
 *   { kind: 'cap',  a, b, r }  — capsule (segment épaissi) ; a = b pour un disque
 *   { kind: 'poly', pts }       — polygone convexe ou non (pastille rectangulaire tournée)
 * Les distances rendues sont des distances BORD À BORD (≤ 0 = contact ou recouvrement).
 */

export const deg2rad = (d) => (d * Math.PI) / 180;
export const r4 = (v) => Math.round(v * 1e4) / 1e4;

export function rotate([x, y], deg) {
  if (!deg) return [x, y];
  const a = deg2rad(deg);
  const c = Math.cos(a), s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
}

const add = (p, q) => [p[0] + q[0], p[1] + q[1]];
const sub = (p, q) => [p[0] - q[0], p[1] - q[1]];
const dot = (p, q) => p[0] * q[0] + p[1] * q[1];
const cross = (p, q) => p[0] * q[1] - p[1] * q[0];
export const dist = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]);

/** Point du repère de l'empreinte → repère de la carte (face dessous = miroir en X). */
export function placePoint(comp, p) {
  const local = comp.side === 'bottom' ? [-p[0], p[1]] : p;
  return add(rotate(local, comp.rot || 0), comp.pos);
}

export const flipLayer = (l) => (l === 'F' ? 'B' : 'F');

/** Pastilles d'un composant posé, en coordonnées carte. `cu` = couches cuivre de la carte
 *  (une traversante métallisée les touche toutes) : F et B par défaut. */
export function worldPads(comp, fp, cu = ['F', 'B']) {
  if (!fp) return [];
  const bottom = comp.side === 'bottom';
  return fp.pads.map((pad, idx) => {
    const tht = !!pad.drill;
    const layer = bottom ? flipLayer(pad.layer || 'F') : pad.layer || 'F';
    return {
      ref: comp.ref,
      compId: comp.id,
      num: pad.num,
      name: pad.name || '',
      key: `${comp.ref}.${pad.num}`,
      idx,
      pos: placePoint(comp, pad.pos),
      size: pad.size,
      rot: (comp.rot || 0) + (bottom ? -(pad.rot || 0) : pad.rot || 0),
      shape: pad.shape,
      drill: pad.drill,
      plated: pad.plated !== false,
      paste: pad.paste !== false && !tht,
      // Un trou non métallisé n'a pas de cuivre (sa « pastille » ne sert qu'au dégagement).
      layers: tht ? (pad.plated === false ? [] : cu) : [layer],
    };
  });
}

/** Coins d'un rectangle centré (w × h) tourné de rot degrés autour de c. */
export function rectCorners(c, [w, h], rot = 0, grow = 0) {
  const hw = w / 2 + grow, hh = h / 2 + grow;
  return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map((p) => add(rotate(p, rot), c));
}

/** Forme de cuivre d'une pastille (grow = marge ajoutée tout autour). */
export function padShape(p, grow = 0) {
  const [w, h] = p.size;
  if (p.shape === 'circle') return { kind: 'cap', a: p.pos, b: p.pos, r: Math.max(w, h) / 2 + grow };
  if (p.shape === 'oval') {
    const long = Math.max(w, h), short = Math.min(w, h);
    const half = (long - short) / 2;
    const axis = rotate(w >= h ? [half, 0] : [0, half], p.rot);
    return { kind: 'cap', a: sub(p.pos, axis), b: add(p.pos, axis), r: short / 2 + grow };
  }
  return { kind: 'poly', pts: rectCorners(p.pos, p.size, p.rot, grow) };
}

export const segShape = (a, b, width) => ({ kind: 'cap', a, b, r: width / 2 });
export const discShape = (c, d) => ({ kind: 'cap', a: c, b: c, r: d / 2 });

/** Rectangle englobant [minx, miny, maxx, maxy] d'une forme. */
export function shapeBox(s) {
  if (s.kind === 'cap') {
    return [Math.min(s.a[0], s.b[0]) - s.r, Math.min(s.a[1], s.b[1]) - s.r, Math.max(s.a[0], s.b[0]) + s.r, Math.max(s.a[1], s.b[1]) + s.r];
  }
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of s.pts) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  return [x0, y0, x1, y1];
}

export const boxesNear = (a, b, m = 0) => a[0] - m <= b[2] && b[0] - m <= a[2] && a[1] - m <= b[3] && b[1] - m <= a[3];

// ── Distances élémentaires ─────────────────────────────────────

export function pointSegDist(p, a, b) {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 === 0) return dist(p, a);
  const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
  return dist(p, [a[0] + ab[0] * t, a[1] + ab[1] * t]);
}

function segsIntersect(a, b, c, d) {
  const d1 = cross(sub(b, a), sub(c, a));
  const d2 = cross(sub(b, a), sub(d, a));
  const d3 = cross(sub(d, c), sub(a, c));
  const d4 = cross(sub(d, c), sub(b, c));
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export function segSegDist(a, b, c, d) {
  if (segsIntersect(a, b, c, d)) return 0;
  return Math.min(pointSegDist(a, c, d), pointSegDist(b, c, d), pointSegDist(c, a, b), pointSegDist(d, a, b));
}

export function pointInPoly(p, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function segPolyDist(a, b, pts) {
  if (pointInPoly(a, pts) || pointInPoly(b, pts)) return 0;
  let m = Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) m = Math.min(m, segSegDist(a, b, pts[j], pts[i]));
  return m;
}

function polyPolyDist(p, q) {
  if (pointInPoly(p[0], q) || pointInPoly(q[0], p)) return 0;
  let m = Infinity;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    for (let k = 0, l = q.length - 1; k < q.length; l = k++) {
      m = Math.min(m, segSegDist(p[j], p[i], q[l], q[k]));
      if (m === 0) return 0;
    }
  }
  return m;
}

/** Distance bord à bord entre deux formes (négative = recouvrement, pour les capsules). */
export function shapeDist(s, t) {
  if (s.kind === 'cap' && t.kind === 'cap') return segSegDist(s.a, s.b, t.a, t.b) - s.r - t.r;
  if (s.kind === 'cap') return segPolyDist(s.a, s.b, t.pts) - s.r;
  if (t.kind === 'cap') return segPolyDist(t.a, t.b, s.pts) - t.r;
  return polyPolyDist(s.pts, t.pts);
}

/** Distance d'une forme au contour de la carte (polygone) : > 0 = à l'intérieur, à cette distance du bord. */
export function shapeEdgeDist(s, outline) {
  let m = Infinity;
  const n = outline.length;
  const probe = s.kind === 'cap' ? s : null;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = outline[j], b = outline[i];
    if (probe) m = Math.min(m, segSegDist(probe.a, probe.b, a, b) - probe.r);
    else for (let k = 0, l = s.pts.length - 1; k < s.pts.length; l = k++) m = Math.min(m, segSegDist(s.pts[l], s.pts[k], a, b));
  }
  const ref = s.kind === 'cap' ? s.a : s.pts[0];
  return pointInPoly(ref, outline) ? m : -Math.abs(m);
}

export function polyBox(pts) {
  return shapeBox({ kind: 'poly', pts });
}

export function polyArea(pts) {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += pts[j][0] * pts[i][1] - pts[i][0] * pts[j][1];
  return a / 2;
}

/** Encombrement (courtyard) d'un composant posé : polygone carte. */
export function courtyardPoly(comp, fp) {
  if (!fp) return null;
  let cy = fp.courtyard;
  if (!cy) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of fp.pads) {
      const r = Math.max(...p.size) / 2;
      x0 = Math.min(x0, p.pos[0] - r); y0 = Math.min(y0, p.pos[1] - r); x1 = Math.max(x1, p.pos[0] + r); y1 = Math.max(y1, p.pos[1] + r);
    }
    if (!Number.isFinite(x0)) return null;
    cy = [[x0 - 0.25, y0 - 0.25], [x1 + 0.25, y1 + 0.25]];
  }
  const [[x0, y0], [x1, y1]] = cy;
  return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map((p) => placePoint(comp, p));
}

/** Emprise au sol d'un obstacle de carte mère (rectangle ou disque) en polygone. */
export function obstaclePoly(o) {
  if (o.kind === 'circle') {
    return Array.from({ length: 24 }, (_, k) => [o.c[0] + o.r * Math.cos((k * Math.PI) / 12), o.c[1] + o.r * Math.sin((k * Math.PI) / 12)]);
  }
  return [[o.min[0], o.min[1]], [o.max[0], o.min[1]], [o.max[0], o.max[1]], [o.min[0], o.max[1]]];
}
